import { useState } from "react";
import {
  MapPin, Phone, Mail, Globe, GraduationCap, Award, Users, BookOpen,
  Trophy, Sparkles, Building2, Calendar, ChevronRight, Star, ArrowUpRight,
  ShieldCheck, BadgeCheck, Image as ImageIcon, Newspaper, Wifi, FlaskConical,
  Microscope, Library, Utensils, Home, Bus, Dumbbell, Heart, Camera
} from "lucide-react";

/**
 * Premium pastel college profile sample — v2
 * Route: /design-sample/college-profile-v2
 * NOT live. Pure preview for stakeholder approval.
 */

/* ---------- Palette ---------- */
const C = {
  ink: "#0F172A",
  text2: "#475569",
  border: "#E2E8F0",
  sky: { bg: "#F0F9FF", ring: "#BAE6FD", icon: "#0369A1", chipBg: "#E0F2FE", chipText: "#075985" },
  mint: { bg: "#ECFDF5", ring: "#A7F3D0", icon: "#047857", chipBg: "#D1FAE5", chipText: "#065F46" },
  peach: { bg: "#FFF7ED", ring: "#FED7AA", icon: "#C2410C", chipBg: "#FFEDD5", chipText: "#9A3412" },
  lilac: { bg: "#FAF5FF", ring: "#DDD6FE", icon: "#6D28D9", chipBg: "#EDE9FE", chipText: "#5B21B6" },
  blush: { bg: "#FFF1F2", ring: "#FBCFE8", icon: "#BE185D", chipBg: "#FCE7F3", chipText: "#9D174D" },
  butter: { bg: "#FEFCE8", ring: "#FDE68A", icon: "#A16207", chipBg: "#FEF3C7", chipText: "#854D0E" },
};

/* ---------- Sample data (SGT University Gurugram) ---------- */
const college = {
  name: "SGT University",
  short: "SGT",
  city: "Gurugram",
  state: "Haryana",
  address: "Chandu-Budhera, Badli Rd, Gurugram, Haryana 122505",
  established: 2013,
  affiliation: "UGC · NAAC A+",
  type: "Private University",
  verified: true,
  logo: "https://images.unsplash.com/photo-1580894732444-8ecded7900cd?auto=format&fit=crop&w=200&q=80",
  cover: "https://images.unsplash.com/photo-1607237138185-eedd9c632b0b?auto=format&fit=crop&w=1600&q=80",
  tagline: "Where Ancient Wisdom Meets Modern Innovation",
  website: "https://sgtuniversity.ac.in",
  email: "admissions@sgtuniversity.ac.in",
  phone: "+91 8447-744-333",
  nirf_rank: 84,
  student_rating: 4.6,
  reviews_count: 1247,

  stats: [
    { label: "Est.", value: "2013", icon: Calendar, color: "sky" },
    { label: "Campus", value: "70 acres", icon: MapPin, color: "mint" },
    { label: "Students", value: "12,400+", icon: Users, color: "peach" },
    { label: "Faculty", value: "820", icon: BookOpen, color: "lilac" },
    { label: "Placements", value: "94% · ₹28 LPA", icon: Trophy, color: "blush" },
    { label: "NIRF 2026", value: "#84", icon: Award, color: "butter" },
  ],

  gallery: [
    "https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=1000&q=80",
    "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&w=1000&q=80",
    "https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1000&q=80",
    "https://images.unsplash.com/photo-1592280771190-3e2e4d571952?auto=format&fit=crop&w=1000&q=80",
    "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1000&q=80",
    "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?auto=format&fit=crop&w=1000&q=80",
  ],

  programs: {
    Engineering: ["B.Tech CSE", "B.Tech AI/ML", "B.Tech ECE", "M.Tech Data Science"],
    Medical: ["MBBS", "BDS", "B.Sc Nursing", "Pharm.D"],
    Management: ["BBA", "MBA · Finance", "MBA · HR", "Executive MBA"],
    Design: ["B.Des Fashion", "B.Des Interior", "M.Des Communication"],
    Law: ["BA LLB (Hons)", "BBA LLB", "LLM Constitutional"],
    Sciences: ["B.Sc Biotech", "M.Sc Physics", "PhD Chemistry"],
  },

  facilities: [
    { name: "24/7 Library",    icon: Library },
    { name: "Research Labs",   icon: FlaskConical },
    { name: "Med Centre",      icon: Heart },
    { name: "Hostels",         icon: Home },
    { name: "Sports Complex",  icon: Dumbbell },
    { name: "Innovation Hub",  icon: Sparkles },
    { name: "Dining Halls",    icon: Utensils },
    { name: "Wi-Fi Campus",    icon: Wifi },
    { name: "Shuttle Service", icon: Bus },
    { name: "Photography Lab", icon: Camera },
    { name: "STEM Centre",     icon: Microscope },
    { name: "Auditorium",      icon: Building2 },
  ],

  achievements: [
    { year: "2026", title: "Ranked #84 in NIRF (Overall Universities)" },
    { year: "2025", title: "NAAC A+ Reaccreditation with 3.61 CGPA" },
    { year: "2024", title: "5 Patents granted in Medical Robotics" },
    { year: "2023", title: "MoU signed with Stanford & TUM" },
  ],

  news: [
    { title: "SGT Faculty of Medicine wins ICMR grant of ₹3.4 Cr", when: "3 days ago", tag: "Research" },
    { title: "Placement 2026: Deloitte, TCS, Wipro top recruiters", when: "1 week ago", tag: "Placements" },
    { title: "Annual TechFest 'Innovision 2026' inaugurated", when: "2 weeks ago", tag: "Campus Life" },
  ],

  alumni: [
    { name: "Dr. Ananya Rao",    role: "AIIMS · Cardiologist", batch: "MBBS 2019" },
    { name: "Rohan Malhotra",     role: "Deloitte · Consultant", batch: "MBA 2021" },
    { name: "Priya Nair",         role: "Google · SDE-3",        batch: "B.Tech 2020" },
  ],
};

/* ---------- Reusable atoms ---------- */
const Overline = ({ children, style }) => (
  <div className="uppercase" style={{ fontSize: 11, letterSpacing: "0.22em", fontWeight: 700, color: C.text2, ...style }}>{children}</div>
);

function SectionCard({ color = "sky", icon: Icon, title, subtitle, children, testId }) {
  const t = C[color];
  return (
    <section
      data-testid={testId}
      className="rounded-[28px] p-6 md:p-8"
      style={{ background: t.bg, border: `1px solid ${t.ring}` }}
    >
      <div className="flex items-start gap-4 mb-5">
        <div className="w-12 h-12 rounded-2xl grid place-items-center shrink-0" style={{ background: "#fff", color: t.icon, boxShadow: `0 4px 14px ${t.ring}80` }}>
          <Icon className="w-5 h-5"/>
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-editorial font-bold text-2xl md:text-3xl leading-tight" style={{ color: C.ink }}>{title}</h2>
          {subtitle && <div className="text-sm mt-1" style={{ color: C.text2 }}>{subtitle}</div>}
        </div>
      </div>
      {children}
    </section>
  );
}

function StatChip({ label, value, icon: Icon, color }) {
  const t = C[color];
  return (
    <div className="rounded-2xl p-4 flex items-center gap-3" style={{ background: t.bg, border: `1px solid ${t.ring}` }}>
      <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: "#fff", color: t.icon }}>
        <Icon className="w-4 h-4"/>
      </div>
      <div className="min-w-0">
        <div className="uppercase" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.text2, fontWeight: 700 }}>{label}</div>
        <div className="font-editorial font-bold text-lg leading-tight" style={{ color: C.ink }}>{value}</div>
      </div>
    </div>
  );
}

function Chip({ children, color = "sky" }) {
  const t = C[color];
  return <span className="px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: t.chipBg, color: t.chipText, border: `1px solid ${t.ring}` }}>{children}</span>;
}

/* ---------- Page ---------- */
export default function CollegeProfileSampleV2() {
  const [tab, setTab] = useState("overview");
  const [lightbox, setLightbox] = useState(null);
  const tabs = [
    { k: "overview",   l: "Overview" },
    { k: "programs",   l: "Programs" },
    { k: "campus",     l: "Campus Life" },
    { k: "placements", l: "Placements" },
    { k: "admissions", l: "Admissions" },
  ];

  return (
    <div style={{ background: "#FBFAF7" }} data-testid="college-profile-v2">
      {/* Sample ribbon */}
      <div className="fixed bottom-5 right-5 z-50 px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold shadow-lg flex items-center gap-2 rounded-full" style={{ background: C.peach.icon, color: "#FFF7ED" }} data-testid="sample-ribbon">
        <Sparkles className="w-3.5 h-3.5"/> Sample preview · v2
      </div>

      {/* ============ HERO (curved pastel gradient) ============ */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-0"
          style={{
            background: `
              radial-gradient(60% 80% at 15% 10%, ${C.sky.bg} 0%, transparent 60%),
              radial-gradient(50% 70% at 85% 5%, ${C.mint.bg} 0%, transparent 60%),
              radial-gradient(45% 60% at 50% 90%, ${C.lilac.bg} 0%, transparent 60%),
              linear-gradient(180deg, #FBFAF7 0%, #FFFFFF 100%)`,
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 pt-12 pb-8">
          <div className="rounded-[36px] bg-white/70 backdrop-blur-md border border-white shadow-sm p-6 md:p-10">
            <div className="grid md:grid-cols-[auto_1fr_auto] gap-6 md:gap-10 items-start">
              {/* Logo */}
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-3xl bg-white border shadow-md p-3 grid place-items-center overflow-hidden" style={{ borderColor: C.border }}>
                <img src={college.logo} alt={`${college.name} logo`} className="max-w-full max-h-full object-contain rounded-2xl"/>
              </div>
              {/* Name + meta */}
              <div className="min-w-0">
                <Overline style={{ color: C.sky.icon }}>{college.type} · Est. {college.established} · {college.affiliation}</Overline>
                <h1 className="font-editorial font-bold mt-3 leading-[1.02]" style={{ fontSize: "clamp(2.2rem, 4.4vw, 3.8rem)", color: C.ink }}>
                  {college.name}
                  {college.verified && (
                    <span className="inline-flex items-center gap-1 align-middle ml-3 text-xs font-semibold px-2.5 py-1.5 rounded-full" style={{ background: C.mint.chipBg, color: C.mint.chipText, border: `1px solid ${C.mint.ring}` }}>
                      <BadgeCheck className="w-3.5 h-3.5"/> Verified
                    </span>
                  )}
                </h1>
                <p className="font-editorial italic mt-3" style={{ fontSize: 18, color: C.text2 }}>{college.tagline}</p>
                <div className="flex flex-wrap items-center gap-3 mt-4 text-sm" style={{ color: C.text2 }}>
                  <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" style={{ color: C.sky.icon }}/>{college.city}, {college.state}</span>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1.5"><Star className="w-4 h-4 fill-current" style={{ color: "#F59E0B" }}/>{college.student_rating} <span className="text-xs" style={{ color: C.text2 }}>({college.reviews_count.toLocaleString()} reviews)</span></span>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1.5"><Trophy className="w-4 h-4" style={{ color: C.peach.icon }}/>NIRF #{college.nirf_rank}</span>
                </div>
              </div>
              {/* CTA */}
              <div className="flex flex-col gap-2">
                <a href={college.website} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm text-white transition-transform hover:-translate-y-0.5" style={{ background: C.ink }} data-testid="visit-btn">
                  Visit site <ArrowUpRight className="w-4 h-4"/>
                </a>
                <button className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm bg-white border" style={{ borderColor: C.border, color: C.ink }} data-testid="brochure-btn">
                  <BookOpen className="w-4 h-4"/> Download brochure
                </button>
                <button className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm" style={{ background: C.peach.bg, color: C.peach.icon, border: `1px solid ${C.peach.ring}` }} data-testid="save-btn">
                  <Heart className="w-4 h-4"/> Save to shortlist
                </button>
              </div>
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-8">
              {college.stats.map(s => <StatChip key={s.label} {...s}/>)}
            </div>
          </div>
        </div>
      </div>

      {/* ============ Tabs ============ */}
      <div className="sticky top-0 z-30 backdrop-blur-md" style={{ background: "rgba(251,250,247,0.9)", borderBottom: `1px solid ${C.border}` }} data-testid="profile-tabs">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center gap-1 overflow-x-auto hide-scrollbar">
          {tabs.map(t => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`tab-${t.k}`}
              className="px-4 py-3 text-sm font-semibold whitespace-nowrap transition-colors relative"
              style={{ color: tab === t.k ? C.ink : C.text2 }}
            >
              {t.l}
              {tab === t.k && <div className="absolute left-2 right-2 -bottom-px h-0.5" style={{ background: C.ink, borderRadius: 4 }}/>}
            </button>
          ))}
        </div>
      </div>

      {/* ============ Body ============ */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="min-w-0 space-y-8">
          {/* About */}
          <SectionCard color="sky" icon={BookOpen} title="About SGT University" subtitle="One of North India's fastest-growing multi-disciplinary universities." testId="section-about">
            <div className="prose-editorial" style={{ color: C.text2, fontSize: 16, lineHeight: 1.75 }}>
              <p style={{ marginBottom: 12 }}>
                Established in 2013, SGT University is a UGC-recognised private research university sprawling 70 acres in the National Capital Region. From MBBS and Engineering to Design and Law, its 40+ constituent institutes host over 12,400 students from 26 states and 14 countries.
              </p>
              <p>
                The university is NAAC A+ accredited and features prominently in the NIRF national rankings. Its Innovation & Entrepreneurship Centre has incubated 87 start-ups since 2020, several backed by DST-SISFS.
              </p>
            </div>
          </SectionCard>

          {/* Programs */}
          <SectionCard color="mint" icon={GraduationCap} title="Programs & Departments" subtitle="42 undergraduate · 38 postgraduate · 24 doctoral programs" testId="section-programs">
            <div className="grid sm:grid-cols-2 gap-4">
              {Object.entries(college.programs).map(([faculty, list], i) => {
                const chipColor = ["sky","peach","lilac","blush","butter","mint"][i % 6];
                return (
                  <div key={faculty} className="rounded-2xl bg-white p-5 border" style={{ borderColor: C.border }}>
                    <div className="font-editorial font-bold text-slate-900 mb-3">{faculty}</div>
                    <div className="flex flex-wrap gap-2">
                      {list.map(p => <Chip key={p} color={chipColor}>{p}</Chip>)}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          {/* Photo Gallery */}
          <SectionCard color="lilac" icon={ImageIcon} title="Campus in pictures" subtitle="Click any photo to open the lightbox" testId="section-gallery">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {college.gallery.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setLightbox(src)}
                  className="group relative aspect-[4/3] overflow-hidden rounded-2xl bg-white shadow-sm ring-1"
                  style={{ ringColor: C.lilac.ring }}
                  data-testid={`gallery-${i}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover transition-transform group-hover:scale-105"/>
                  <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/20 transition-colors"/>
                </button>
              ))}
            </div>
          </SectionCard>

          {/* Facilities */}
          <SectionCard color="peach" icon={Building2} title="Campus facilities" subtitle="Modern infrastructure across 70 acres" testId="section-facilities">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {college.facilities.map(({ name, icon: Icon }, i) => {
                const t = C.peach;
                return (
                  <div key={name} className="rounded-2xl bg-white p-4 flex items-center gap-3 border transition-transform hover:-translate-y-0.5" style={{ borderColor: t.ring }}>
                    <div className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ background: t.chipBg, color: t.icon }}>
                      <Icon className="w-4 h-4"/>
                    </div>
                    <div className="font-editorial font-semibold text-sm" style={{ color: C.ink }}>{name}</div>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          {/* Achievements timeline */}
          <SectionCard color="butter" icon={Award} title="Milestones" testId="section-milestones">
            <ol className="space-y-4">
              {college.achievements.map((a, i) => (
                <li key={i} className="flex gap-4 items-start">
                  <div className="w-16 shrink-0">
                    <div className="rounded-xl bg-white grid place-items-center py-2 border" style={{ borderColor: C.butter.ring }}>
                      <span className="font-editorial font-bold" style={{ color: C.butter.icon }}>{a.year}</span>
                    </div>
                  </div>
                  <div className="flex-1 rounded-2xl bg-white p-4 border" style={{ borderColor: C.border, color: C.ink }}>
                    <div className="font-editorial font-semibold">{a.title}</div>
                  </div>
                </li>
              ))}
            </ol>
          </SectionCard>

          {/* Alumni */}
          <SectionCard color="blush" icon={Users} title="Notable alumni" testId="section-alumni">
            <div className="grid sm:grid-cols-3 gap-4">
              {college.alumni.map(al => (
                <div key={al.name} className="rounded-2xl bg-white p-5 border text-center" style={{ borderColor: C.blush.ring }}>
                  <div className="w-14 h-14 mx-auto rounded-full grid place-items-center font-editorial font-bold text-xl" style={{ background: C.blush.chipBg, color: C.blush.chipText }}>
                    {al.name.split(" ").map(x => x[0]).slice(0,2).join("")}
                  </div>
                  <div className="font-editorial font-bold mt-3" style={{ color: C.ink }}>{al.name}</div>
                  <div className="text-xs mt-1" style={{ color: C.text2 }}>{al.role}</div>
                  <div className="text-[11px] uppercase tracking-[0.18em] mt-2" style={{ color: C.blush.chipText }}>{al.batch}</div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Recent news */}
          <SectionCard color="sky" icon={Newspaper} title="Recent from SGT" testId="section-news">
            <div className="space-y-3">
              {college.news.map((n, i) => (
                <a key={i} href="#" className="flex items-center gap-4 rounded-2xl bg-white p-4 border transition-all hover:-translate-y-0.5" style={{ borderColor: C.border }} data-testid={`news-${i}`}>
                  <div className="rounded-xl px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em]" style={{ background: C.sky.chipBg, color: C.sky.chipText }}>{n.tag}</div>
                  <div className="font-editorial font-semibold flex-1 min-w-0" style={{ color: C.ink }}>{n.title}</div>
                  <div className="text-xs shrink-0" style={{ color: C.text2 }}>{n.when}</div>
                  <ChevronRight className="w-4 h-4 shrink-0" style={{ color: C.text2 }}/>
                </a>
              ))}
            </div>
          </SectionCard>
        </div>

        {/* ============ Sidebar ============ */}
        <aside className="space-y-6 lg:sticky lg:top-20 h-fit">
          <div className="rounded-[28px] p-6 bg-white border" style={{ borderColor: C.border }} data-testid="sidebar-contact">
            <Overline style={{ color: C.sky.icon }}>Contact & visit</Overline>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-start gap-2"><Globe className="w-4 h-4 mt-0.5" style={{ color: C.sky.icon }}/><a href={college.website} target="_blank" rel="noreferrer" className="hover:underline break-all" style={{ color: C.sky.icon }}>{college.website.replace("https://","")}</a></li>
              <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5" style={{ color: C.sky.icon }}/><a href={`mailto:${college.email}`} className="hover:underline break-all" style={{ color: C.ink }}>{college.email}</a></li>
              <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5" style={{ color: C.sky.icon }}/><a href={`tel:${college.phone}`} className="hover:underline" style={{ color: C.ink }}>{college.phone}</a></li>
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5" style={{ color: C.sky.icon }}/><span style={{ color: C.ink }}>{college.address}</span></li>
            </ul>
            <button className="w-full mt-4 py-3 rounded-full font-semibold text-sm text-white transition-transform hover:-translate-y-0.5" style={{ background: C.ink }} data-testid="apply-btn">
              Apply for 2026 admission
            </button>
          </div>

          {/* Rating card */}
          <div className="rounded-[28px] p-6" style={{ background: C.butter.bg, border: `1px solid ${C.butter.ring}` }} data-testid="sidebar-rating">
            <Overline style={{ color: C.butter.icon }}>Student rating</Overline>
            <div className="flex items-baseline gap-3 mt-3">
              <div className="font-editorial font-bold" style={{ fontSize: 44, color: C.ink, lineHeight: 1 }}>{college.student_rating}</div>
              <div className="flex" style={{ color: "#F59E0B" }}>
                {[1,2,3,4,5].map(n => <Star key={n} className={`w-4 h-4 ${n <= Math.round(college.student_rating) ? "fill-current" : ""}`}/>)}
              </div>
            </div>
            <div className="text-xs mt-2" style={{ color: C.text2 }}>Based on {college.reviews_count.toLocaleString()} verified student reviews</div>
            <a href="#" className="text-xs font-semibold mt-3 inline-flex items-center gap-1" style={{ color: C.butter.icon }}>Read reviews <ArrowUpRight className="w-3 h-3"/></a>
          </div>

          {/* Claim */}
          {!college.verified && (
            <div className="rounded-[28px] p-6" style={{ background: C.peach.bg, border: `1px solid ${C.peach.ring}` }}>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" style={{ color: C.peach.icon }}/><Overline style={{ color: C.peach.icon }}>Claim this profile</Overline></div>
              <p className="text-sm mt-3" style={{ color: C.text2 }}>Are you from {college.name}? Get a Verified badge and keep your info accurate.</p>
              <button className="w-full mt-4 py-2.5 rounded-full font-semibold text-sm" style={{ background: C.peach.icon, color: "#FFF7ED" }}>Claim profile</button>
            </div>
          )}

          {/* Similar */}
          <div className="rounded-[28px] p-6 bg-white border" style={{ borderColor: C.border }} data-testid="sidebar-similar">
            <Overline style={{ color: C.lilac.icon }}>You might also like</Overline>
            <ul className="mt-4 space-y-3">
              {["Manav Rachna International", "Ashoka University", "OP Jindal Global"].map(name => (
                <li key={name}>
                  <a href="#" className="flex items-center justify-between gap-3 text-sm hover:underline" style={{ color: C.ink }}>
                    <span className="font-editorial font-semibold">{name}</span>
                    <ChevronRight className="w-4 h-4" style={{ color: C.text2 }}/>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-md grid place-items-center p-4" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <img src={lightbox} alt="" className="max-w-[92vw] max-h-[88vh] rounded-2xl shadow-2xl"/>
        </div>
      )}

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { scrollbar-width: none; }
      `}</style>
    </div>
  );
}
